/**
 * NavigatorX Delete User Modal
 * 
 * Confirmation modal for permanently deleting a user.
 * Handles cascade deletion of all user subcollections.
 * 
 * Deleted:
 * - User document
 * - All favorites
 * - All well history
 * - All referral invites
 * - All referrals
 * - All notifications
 * - All hazard reports
 * - All verification data
 * - All cancellation feedback
 * 
 * @author Claude AI for TGE.LA
 * @version 1.0.0
 */

import { useState } from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import {
  X,
  Trash2,
  Loader2,
  AlertTriangle,
  User,
  Star,
  Navigation,
  Gift,
  Bell,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { deleteUser } from '../../../utils/services/navigatorXService';
import { auth } from '../../../utils/firebase/client';
import type { NavUser } from '../../../types/navigatorx';

interface DeleteUserModalProps {
  user: NavUser;
  onClose: () => void;
  onDelete: (userId: string) => void;
}

export default function DeleteUserModal({ user, onClose, onDelete }: DeleteUserModalProps) {
  const [deleting, setDeleting] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  
  // Require typing "DELETE" to confirm
  const confirmRequired = 'DELETE';
  const canDelete = confirmText === confirmRequired;

  const handleDelete = async () => {
    if (!canDelete) {
      toast.error(`Please type "${confirmRequired}" to confirm deletion`);
      return;
    }

    setDeleting(true);

    try {
      const adminId = auth.currentUser?.uid || 'unknown';
      
      await deleteUser(user.id, adminId);
      
      toast.success(`Permanently deleted ${user.displayName || user.email}`);
      onDelete(user.id);
      onClose();
    } catch (error: any) {
      console.error('Error deleting user:', error);
      toast.error(error.message || 'Failed to delete user');
    } finally {
      setDeleting(false);
    }
  };

  // Calculate what will be deleted
  const deletionSummary = [
    { icon: User, label: 'User profile & settings', count: 1 },
    { icon: Star, label: 'Saved favorites', count: user.favoritesCount || 0 },
    { icon: Navigation, label: 'Navigation history', count: '?' },
    { icon: Gift, label: 'Referral data', count: user.referralCount || 0 },
    { icon: Bell, label: 'Notifications', count: '?' },
    { icon: AlertCircle, label: 'Hazard reports', count: user.hazardsReported || 0 }
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-red-500 to-red-600 p-4 text-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-6 w-6" />
            <div>
              <h2 className="text-lg font-semibold">Delete User</h2>
              <p className="text-red-100 text-sm">This action cannot be undone</p>
            </div>
          </div>
          <button 
            onClick={onClose} 
            className="hover:bg-white/20 p-1 rounded"
            disabled={deleting}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* User Info */}
          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
              <span className="text-red-600 font-bold text-lg">
                {user.displayName?.charAt(0) || user.email?.charAt(0) || '?'}
              </span>
            </div>
            <div>
              <p className="font-medium">{user.displayName || 'No Name'}</p>
              <p className="text-sm text-gray-500">{user.email || user.phone}</p>
              {user.company && (
                <p className="text-xs text-gray-400">{user.company}</p>
              )}
            </div>
          </div>

          {/* Warning */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex gap-3">
              <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-semibold text-red-800">
                  Permanent Deletion Warning
                </p>
                <p className="text-red-700 mt-1">
                  This will permanently delete the user and ALL associated data. 
                  This action cannot be reversed.
                </p>
              </div>
            </div>
          </div>

          {/* What will be deleted */}
          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">
              The following data will be permanently deleted:
            </p>
            <div className="space-y-2">
              {deletionSummary.map(({ icon: Icon, label, count }) => (
                <div key={label} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2 text-gray-600">
                    <Icon className="h-4 w-4 text-gray-400" />
                    {label}
                  </div>
                  <span className="text-gray-400 font-mono text-xs">
                    {typeof count === 'number' && count > 0 ? count : '-'}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Confirmation Input */}
          <div className="pt-4 border-t">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Type <code className="bg-red-100 text-red-800 px-1 rounded">{confirmRequired}</code> to confirm:
            </label>
            <Input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
              placeholder={confirmRequired}
              className={`font-mono ${canDelete ? 'border-red-500 bg-red-50' : ''}`}
              disabled={deleting}
              autoFocus
            />
          </div>
        </div>

        {/* Footer */}
        <div className="border-t p-4 flex justify-end gap-3 bg-gray-50">
          <Button 
            variant="outline" 
            onClick={onClose}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            disabled={deleting || !canDelete}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            {deleting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Deleting...
              </>
            ) : (
              <>
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Permanently
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
